import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReviewerShell from '@/components/reviewer/ReviewerShell';
import ScriptHighlightPane from '@/components/reviewer/ScriptHighlightPane';
import {
  checkpointReview,
  finalizeReview,
  getReview,
  getScriptText,
  type UndoFrame,
} from '@/services/mlReviewService';
import {
  acceptedLabelSpans,
  applySpanToItem,
  blankListItem,
  countMergedMentions,
  isProperFirstName,
  itemNeedles,
  itemTitle,
  neighborCursor,
  relatedCharacterCandidates,
  resolveSpans,
  type ReviewUnit,
  type ScriptSpan,
} from '@/lib/mlReviewUnits';

function currentItem(review: Record<string, unknown>, unit: ReviewUnit | undefined, itemIndex: number): unknown {
  if (!unit) return null;
  if (unit.kind === 'scalar') return review[unit.key];
  const items = (review[unit.key] as unknown[]) || [];
  return items[itemIndex];
}

function snapshot<T>(value: T): T {
  return JSON.parse(JSON.stringify(value ?? null)) as T;
}

function undoDepthFrom(review: Record<string, unknown>): number {
  const stack = (review._qa_review as { undo_stack?: unknown[] } | undefined)?.undo_stack;
  return Array.isArray(stack) ? stack.length : 0;
}

export default function ReviewerWorkspace() {
  const { scriptId = '', model = '' } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState(scriptId);
  const [scriptText, setScriptText] = useState('');
  const [review, setReview] = useState<Record<string, unknown>>({});
  const [units, setUnits] = useState<ReviewUnit[]>([]);
  const [unitIndex, setUnitIndex] = useState(0);
  const [itemIndex, setItemIndex] = useState(0);
  const [editText, setEditText] = useState('');
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [draftSpan, setDraftSpan] = useState<ScriptSpan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [skippedNeedles, setSkippedNeedles] = useState<Set<string>>(new Set());
  const [mergeSelected, setMergeSelected] = useState<Set<string>>(new Set());
  const [undoDepth, setUndoDepth] = useState(0);

  const unit = units[unitIndex];
  const listKey = unit?.kind === 'list' ? unit.key : null;
  const item = currentItem(review, unit, itemIndex);
  const listLen = unit?.kind === 'list' ? ((review[unit.key] as unknown[]) || []).length : 1;

  const load = useCallback(async () => {
    const [script, payload] = await Promise.all([getScriptText(scriptId), getReview(model, scriptId)]);
    setTitle(script.title);
    setScriptText(script.text);
    setReview(payload.review);
    setUnits(payload.units);
    const resume = (payload.review._qa_review as { resume?: { unit_index?: number; item_index?: number } } | undefined)
      ?.resume;
    setUnitIndex(resume?.unit_index ?? 0);
    setItemIndex(resume?.item_index ?? 0);
    setDone(payload.review._label_status === 'gold');
    setSkippedNeedles(new Set());
    setUndoDepth(undoDepthFrom(payload.review));
  }, [model, scriptId]);

  useEffect(() => {
    void load().catch((err: unknown) => setError(err instanceof Error ? err.message : 'Load failed'));
  }, [load]);

  useEffect(() => {
    setEditText(JSON.stringify(item ?? '', null, 2));
    setEditing(false);
    setAdding(false);
    setDraftSpan(null);
  }, [unitIndex, itemIndex, review]);

  const spans = useMemo(() => {
    const cur = resolveSpans(
      scriptText,
      unit?.kind === 'list' ? item : null,
      unit?.kind === 'scalar' ? item : undefined,
    );
    const nextC = neighborCursor(units, review, unitIndex, itemIndex, 1);
    const spanFor = (c: { unitIndex: number; itemIndex: number } | null): ScriptSpan[] => {
      if (!c) return [];
      const u = units[c.unitIndex];
      const it = currentItem(review, u, c.itemIndex);
      return resolveSpans(scriptText, u?.kind === 'list' ? it : null, u?.kind === 'scalar' ? it : undefined);
    };
    return {
      accepted: acceptedLabelSpans(scriptText, units, review, unitIndex, itemIndex, skippedNeedles),
      current: cur,
      next: spanFor(nextC),
    };
  }, [scriptText, review, units, unit, item, unitIndex, itemIndex, skippedNeedles]);

  const characterName =
    listKey === 'characters' && item && typeof item === 'object'
      ? String((item as Record<string, unknown>).name || '').trim()
      : '';
  const mergeCandidates = useMemo(() => {
    if (listKey !== 'characters' || adding || editing || !isProperFirstName(characterName)) return [];
    return relatedCharacterCandidates(
      characterName,
      (review.characters as unknown[]) || [],
      scriptText,
      itemIndex,
    );
  }, [listKey, adding, editing, characterName, review, scriptText, itemIndex]);
  const mergeKey = mergeCandidates.map((candidate) => candidate.name).join('|');
  const selectedMergeNames = mergeKey
    ? mergeKey.split('|').filter((name) => mergeSelected.has(name))
    : [];
  const mergedTotal =
    characterName && selectedMergeNames.length
      ? countMergedMentions(scriptText, characterName, selectedMergeNames)
      : 0;

  useEffect(() => {
    setMergeSelected(new Set(mergeKey ? mergeKey.split('|') : []));
  }, [unitIndex, itemIndex, mergeKey]);

  const persistCursor = async (
    nextUnit: number,
    nextItem: number,
    action: 'accept' | 'skip' | 'cursor' | 'edit' | 'delete' | 'add' | 'merge' | 'undo' = 'cursor',
    extra: Record<string, unknown> = {},
  ) => {
    setBusy(true);
    try {
      const nextUnitDef = units[nextUnit];
      const result = await checkpointReview(model, scriptId, {
        action,
        unit_index: nextUnit,
        list_key: nextUnitDef?.kind === 'list' ? nextUnitDef.key : null,
        item_index: nextItem,
        ...extra,
      });
      setReview(result.review);
      setUnits(result.units);
      setUnitIndex(typeof result.unit_index === 'number' ? result.unit_index : nextUnit);
      setItemIndex(typeof result.item_index === 'number' ? result.item_index : nextItem);
      setUndoDepth(typeof result.undo_depth === 'number' ? result.undo_depth : undoDepthFrom(result.review));
      setEditing(false);
      setAdding(false);
      setDraftSpan(null);
      const skipped = result.undone?.skipped_needles;
      if (skipped?.length) {
        setSkippedNeedles((prev) => {
          const next = new Set(prev);
          skipped.forEach((needle) => next.delete(needle.toLowerCase()));
          return next;
        });
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const advance = (fromUnit = unitIndex, fromItem = itemIndex) => {
    const nxt = neighborCursor(units, review, fromUnit, fromItem, 1);
    if (!nxt) return null;
    return nxt;
  };

  const onAccept = async () => {
    const frame: UndoFrame = {
      kind: 'accept',
      unit_index: unitIndex,
      item_index: itemIndex,
      list_key: listKey,
    };
    const nxt = advance();
    if (!nxt) {
      await persistCursor(unitIndex, itemIndex, 'accept', { undo_frame: frame });
      return;
    }
    await persistCursor(nxt.unitIndex, nxt.itemIndex, 'accept', { undo_frame: frame });
  };

  const onSkip = async () => {
    const needles = itemNeedles(item, unit?.kind === 'scalar' ? item : undefined);
    if (needles.length) {
      setSkippedNeedles((prev) => {
        const next = new Set(prev);
        needles.forEach((n) => next.add(n.toLowerCase()));
        return next;
      });
    }
    const nxt = advance();
    if (!nxt) return;
    await persistCursor(nxt.unitIndex, nxt.itemIndex, 'skip', {
      undo_frame: {
        kind: 'skip',
        unit_index: unitIndex,
        item_index: itemIndex,
        list_key: listKey,
        skipped_needles: needles.map((needle) => needle.toLowerCase()),
      },
    });
  };

  const onDelete = async () => {
    if (!listKey) return;
    await persistCursor(unitIndex, itemIndex, 'delete', {
      undo_frame: {
        kind: 'delete',
        unit_index: unitIndex,
        item_index: itemIndex,
        list_key: listKey,
        item: snapshot(item),
      },
    });
  };

  const onUndo = async () => {
    if (!undoDepth) return;
    await persistCursor(unitIndex, itemIndex, 'undo');
  };

  const startAdd = () => {
    if (!listKey) return;
    setError(null);
    setAdding(true);
    setEditing(true);
    setDraft(blankListItem(listKey));
    setDraftSpan(null);
  };

  const startEdit = () => {
    setError(null);
    setEditing(true);
    setAdding(false);
    if (listKey) {
      setDraft(blankListItem(listKey));
    }
    setDraftSpan(null);
  };

  const canAdd = Boolean(listKey) && (adding || editing);
  const onSelectSpan = (span: ScriptSpan | null) => {
    if (!span || !listKey || !canAdd) return;
    setDraftSpan(span);
    setAdding(true);
    setDraft((prev) => applySpanToItem(listKey, Object.keys(prev).length ? prev : blankListItem(listKey), scriptText, span));
  };

  const onSaveEdit = async () => {
    try {
      const parsed = JSON.parse(editText);
      if (unit?.kind === 'list') {
        await persistCursor(unitIndex, itemIndex, 'edit', {
          item: parsed,
          undo_frame: {
            kind: 'edit',
            unit_index: unitIndex,
            item_index: itemIndex,
            list_key: listKey,
            item: snapshot(item),
          },
        });
      } else if (unit) {
        await persistCursor(unitIndex, itemIndex, 'edit', {
          scalar_key: unit.key,
          scalar_value: parsed,
          undo_frame: {
            kind: 'edit',
            unit_index: unitIndex,
            item_index: itemIndex,
            scalar_key: unit.key,
            scalar_value: snapshot(item),
          },
        });
      }
      setEditing(false);
    } catch {
      setError('Edit must be valid JSON');
    }
  };

  const onMerge = async () => {
    if (!listKey || !selectedMergeNames.length) return;
    await persistCursor(unitIndex, itemIndex, 'merge', {
      list_key: 'characters',
      merge_names: selectedMergeNames,
      interaction_count: mergedTotal,
      note: 'merge surname variants',
      undo_frame: {
        kind: 'merge',
        unit_index: unitIndex,
        item_index: itemIndex,
        list_key: 'characters',
        items: snapshot((review.characters as unknown[]) || []),
      },
    });
  };

  const onSaveNew = async () => {
    if (!listKey) return;
    const nameOrText = String(draft.name ?? draft.text ?? draft.summary ?? draft.statement ?? draft.dialogue_text ?? '').trim();
    if (!nameOrText) {
      setError('Give the new label a name or text, or highlight it in the script');
      return;
    }
    await persistCursor(unitIndex, itemIndex, 'add', {
      item: draft,
      note: 'add missing label',
      undo_frame: {
        kind: 'add',
        unit_index: unitIndex,
        item_index: itemIndex,
        list_key: listKey,
      },
    });
    setAdding(false);
    setEditing(false);
    setDraftSpan(null);
  };

  const onFinalize = async () => {
    setBusy(true);
    try {
      const result = await finalizeReview(model, scriptId);
      setReview(result.review);
      setDone(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Finalize failed');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (editing || adding) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'a' || e.key === 'A') {
        e.preventDefault();
        void onAccept();
      } else if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        startEdit();
      } else if (e.key === 'n' || e.key === 'N' || e.key === '+') {
        e.preventDefault();
        startAdd();
      } else if (e.key === 'u' || e.key === 'U') {
        e.preventDefault();
        void onUndo();
      } else if (e.key === 'q' || e.key === 'Q') {
        e.preventDefault();
        navigate('/reviewer');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const atEnd = !advance();
  const draftPreview = draftSpan
    ? scriptText.slice(draftSpan.start, draftSpan.end).replace(/\s+/g, ' ').trim()
    : '';

  return (
    <ReviewerShell subtitle={`${title} · ${model}`}>
      <div className="grid gap-4 lg:grid-cols-[minmax(320px,420px)_1fr] lg:h-[calc(100vh-8rem)] lg:min-h-0">
        <section className="ni-panel p-4 flex flex-col min-h-0">
          <p className="ni-label">
            {unit?.label || 'Unit'} · {unit?.kind === 'list' ? `${itemIndex + 1} / ${listLen}` : 'scalar'} · unit{' '}
            {unitIndex + 1}/{units.length}
          </p>
          <h2 className="text-lg font-sans font-semibold mt-1">
            {adding
              ? 'New label'
              : unit?.kind === 'list'
                ? itemTitle(unit.key, item, itemIndex)
                : unit?.label}
          </h2>
          {error && <p className="text-red-300 text-sm mt-2">{error}</p>}
          {done && <p className="text-emerald-300 text-sm mt-2">This model is gold.</p>}

          <pre className="mt-3 flex-1 overflow-auto ni-scroll text-[11px] font-mono text-white/75 bg-black/40 p-3 rounded-sm">
            {JSON.stringify(adding ? draft : item ?? null, null, 2)}
          </pre>

          {mergeCandidates.length > 0 && (
            <div className="mt-3 rounded-sm border border-amber-400/25 bg-amber-400/5 p-3 space-y-2">
              <p className="text-[10px] font-mono uppercase tracking-wider text-amber-200/80">
                Merge surname variants
              </p>
              <p className="text-xs font-sans text-white/55">
                {characterName} also appears with a surname. Merge selected names into this
                character and add their counts together.
              </p>
              <ul className="space-y-1.5">
                {mergeCandidates.map((candidate) => (
                  <li key={candidate.name}>
                    <label className="flex items-start gap-2 text-sm font-sans text-white/80">
                      <input
                        type="checkbox"
                        checked={mergeSelected.has(candidate.name)}
                        onChange={() => {
                          setMergeSelected((prev) => {
                            const next = new Set(prev);
                            if (next.has(candidate.name)) next.delete(candidate.name);
                            else next.add(candidate.name);
                            return next;
                          });
                        }}
                        className="mt-1"
                      />
                      <span>
                        <span className="font-mono text-white">{candidate.name}</span>
                        <span className="text-white/45"> · {candidate.scriptCount} in script</span>
                        {candidate.inInventory && (
                          <span className="text-sky-300/70"> · also a separate label</span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              {selectedMergeNames.length > 0 && (
                <p className="text-xs font-mono text-amber-100/90">
                  {characterName} + {selectedMergeNames.join(' + ')} = {mergedTotal} occurrences
                </p>
              )}
              <button
                type="button"
                disabled={busy || done || selectedMergeNames.length === 0}
                onClick={() => void onMerge()}
                className="rounded-sm bg-amber-400/20 px-3 py-1.5 text-sm hover:bg-amber-400/30 disabled:opacity-40"
              >
                Merge selected
              </button>
            </div>
          )}

          {editing && !adding && (
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="mt-3 h-32 w-full rounded-sm border border-[#2a2a2a] bg-black p-2 font-mono text-[11px]"
            />
          )}

          {canAdd && listKey && (
            <div className="mt-3 space-y-3 flex-1 min-h-0 overflow-auto ni-scroll">
              <p className="text-sm font-sans text-white/60">
                Highlight the missing span in the script (for example JACK GOODMAN), then save.
              </p>
              {listKey === 'characters' ? (
                <>
                  <label className="block">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-white/45">Name</span>
                    <input
                      value={String(draft.name ?? '')}
                      onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
                      className="mt-1 w-full rounded-sm border border-[#2a2a2a] bg-black px-2 py-1.5 text-sm"
                      placeholder="JACK GOODMAN"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-white/45">
                      Aliases (comma-separated)
                    </span>
                    <input
                      value={Array.isArray(draft.aliases) ? (draft.aliases as string[]).join(', ') : ''}
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          aliases: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                        }))
                      }
                      className="mt-1 w-full rounded-sm border border-[#2a2a2a] bg-black px-2 py-1.5 text-sm"
                      placeholder="JACK"
                    />
                  </label>
                </>
              ) : (
                <label className="block">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-white/45">Label text</span>
                  <textarea
                    value={String(
                      draft.text ?? draft.summary ?? draft.statement ?? draft.dialogue_text ?? draft.cause_event_text ?? '',
                    )}
                    onChange={(e) => {
                      const field =
                        'text' in draft
                          ? 'text'
                          : 'summary' in draft
                            ? 'summary'
                            : 'statement' in draft
                              ? 'statement'
                              : 'dialogue_text' in draft
                                ? 'dialogue_text'
                                : 'cause_event_text';
                      setDraft((prev) => ({ ...prev, [field]: e.target.value }));
                    }}
                    className="mt-1 h-24 w-full rounded-sm border border-[#2a2a2a] bg-black p-2 text-sm"
                  />
                </label>
              )}
              <p className="text-[11px] font-mono text-emerald-200/80">
                {draftSpan
                  ? `Span ${draftSpan.start}–${draftSpan.end}: “${draftPreview.slice(0, 80)}${draftPreview.length > 80 ? '…' : ''}”`
                  : 'No script span yet — select text on the right.'}
              </p>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy || done}
              onClick={() => void onAccept()}
              className="rounded-sm bg-amber-400/20 px-3 py-1.5 text-sm hover:bg-amber-400/30"
            >
              Accept
            </button>
            {!editing ? (
              <button
                type="button"
                disabled={busy || done}
                onClick={startEdit}
                className="rounded-sm bg-white/10 px-3 py-1.5 text-sm"
              >
                Edit
              </button>
            ) : (
              <button
                type="button"
                disabled={busy}
                onClick={() => void onSaveEdit()}
                className="rounded-sm bg-white/10 px-3 py-1.5 text-sm"
              >
                Save edit
              </button>
            )}
            {canAdd && (
              <button
                type="button"
                disabled={busy || done}
                onClick={() => void onSaveNew()}
                className="rounded-sm bg-emerald-500/25 px-3 py-1.5 text-sm"
              >
                Save new label
              </button>
            )}
            {unit?.kind === 'list' && !editing && (
              <button
                type="button"
                disabled={busy || done}
                onClick={startAdd}
                className="rounded-sm bg-emerald-500/15 px-3 py-1.5 text-sm"
              >
                Add label
              </button>
            )}
            {unit?.kind === 'list' && !adding && (
              <button
                type="button"
                disabled={busy || done}
                onClick={() => void onDelete()}
                className="rounded-sm bg-red-500/15 px-3 py-1.5 text-sm"
              >
                Delete
              </button>
            )}
            <button
              type="button"
              disabled={busy || done || undoDepth < 1}
              onClick={() => void onUndo()}
              className="rounded-sm bg-white/10 px-3 py-1.5 text-sm disabled:opacity-40"
            >
              Undo
            </button>
            {(editing || adding) && (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setEditing(false);
                  setAdding(false);
                  setDraftSpan(null);
                }}
                className="rounded-sm bg-white/5 px-3 py-1.5 text-sm"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              disabled={busy || done}
              onClick={() => void onSkip()}
              className="rounded-sm bg-white/5 px-3 py-1.5 text-sm"
            >
              Skip
            </button>
            <Link to="/reviewer" className="rounded-sm bg-white/5 px-3 py-1.5 text-sm">
              Panel
            </Link>
          </div>

          {canAdd && (
            <p className="mt-3 text-xs font-sans text-white/45">
              Highlight the missing span in the script, then <span className="text-white/70">Save new label</span>.
            </p>
          )}

          {atEnd && !done && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void onFinalize()}
              className="mt-4 rounded-sm bg-emerald-500/20 px-3 py-2 text-sm"
            >
              Mark this model gold
            </button>
          )}
        </section>

        <ScriptHighlightPane
          text={scriptText}
          accepted={spans.accepted}
          current={spans.current}
          next={spans.next}
          draft={canAdd ? draftSpan : null}
          captureSelection={canAdd}
          statusLabel={`${unit?.label || 'Unit'} · ${unit?.kind === 'list' ? `${itemIndex + 1} / ${listLen}` : 'scalar'} · unit ${unitIndex + 1}/${units.length || 1}`}
          onSelectSpan={onSelectSpan}
        />
      </div>
    </ReviewerShell>
  );
}
